import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Permission } from '@/shared/types/permissions';
import type { User, SessionOrganization } from '@/shared/types/entities';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  orgId: string | null;
  organizations: SessionOrganization[];
  permissions: Permission[];
  isAdmin: boolean;
  isLoadingSession: boolean;
  sessionHydrated: boolean;
}

interface AuthActions {
  login: (user: User, permissions: Permission[], orgId?: string) => void;
  setSession: (params: {
    user: User | null;
    permissions: Permission[];
    orgId: string | null;
    organizations: SessionOrganization[];
  }) => void;
  logout: () => void;
  setOrg: (orgId: string) => void;
  setPermissions: (permissions: Permission[]) => void;
  setSessionLoading: (loading: boolean) => void;
  setSessionHydrated: (hydrated: boolean) => void;
  hasPermission: (permission: Permission | Permission[]) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  orgId: null,
  organizations: [],
  permissions: [],
  isAdmin: false,
  isLoadingSession: true,
  sessionHydrated: false,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      login: (user, permissions, orgId) => {
        set({
          isAuthenticated: true,
          user,
          permissions,
          orgId: orgId ?? null,
          organizations: [],
          isAdmin: permissions.includes('ADMIN_ACCESS'),
          isLoadingSession: false,
          sessionHydrated: true,
        });
      },

      setSession: ({ user, permissions, orgId, organizations }) => {
        set({
          isAuthenticated: !!user || organizations.length > 0,
          user,
          permissions,
          orgId,
          organizations,
          isAdmin: permissions.includes('ADMIN_ACCESS'),
          isLoadingSession: false,
          sessionHydrated: true,
        });
      },

      logout: () => {
        set({
          ...initialState,
          isLoadingSession: false,
          sessionHydrated: true,
        });
      },

      setOrg: (orgId) => set({ orgId }),

      setPermissions: (permissions) =>
        set({
          permissions,
          isAdmin: permissions.includes('ADMIN_ACCESS'),
        }),

      setSessionLoading: (loading) => set({ isLoadingSession: loading }),

      setSessionHydrated: (hydrated) => set({ sessionHydrated: hydrated }),

      hasPermission: (permission) => {
        const { permissions } = get();
        if (Array.isArray(permission)) {
          return permission.some((p) => permissions.includes(p));
        }
        return permissions.includes(permission);
      },

      hasAnyPermission: (required) => {
        const { permissions } = get();
        return required.some((p) => permissions.includes(p));
      },

      hasAllPermissions: (required) => {
        const { permissions } = get();
        return required.every((p) => permissions.includes(p));
      },
    }),
    {
      name: 'promote-auth-storage',
      partialize: (state) => ({
        orgId: state.orgId,
        organizations: state.organizations,
      }),
    },
  ),
);
